import { useState } from "react";
import { formLabel, resolvePreset, sizePresets, storedSizes } from "./techpack_form_contract.js";
import * as Y from "yjs";
import { type FieldEnvelope } from "../schema.js";
import { useStudio } from "./StudioContext.js";
import { Section, t, useYField } from "./form_primitives.js";
import {
  dimmedMeasurementKeys,
  studioMeasurementKey,
} from "../generated/shared/types/canonical_techpack_fields.js";

// ---------------------------------------------------------------------------
// 3. MEASUREMENTS (CM)
// ---------------------------------------------------------------------------

const MEAS: [string, string][] = [
  ["A_front_length", "A  FRONT LENGTH"],  ["B_back_length",  "B  BACK LENGTH"],
  ["C_shoulder",     "C  SHOULDER"],      ["D_chest",        "D  CHEST"],
  ["E_waist",        "E  WAIST"],         ["F_hip",          "F  HIP"],
  ["G_hem",          "G  HEM"],           ["H_sleeve_length","H  SLEEVE LENGTH"],
  ["I_sleeve_width", "I  Bicep Circumference / 袖肥"],  ["J_cuff",         "J  CUFF"],
  ["K_collar_width", "K  COLLAR WIDTH"],               ["L_waist_height", "L  WAIST HEIGHT"],
  ["M_front_rise",   "M  FRONT RISE"],                 ["N_back_rise",    "N  BACK RISE"],
  ["O_inseam",             "O  Inseam / 内裤长"],
  ["P_side_seam",          "P  Side Seam / 侧缝长"],
  ["Q_skirt_length_cf",    "Q  Skirt Length CF / 前裙长"],
  ["R_skirt_length_cb",    "R  Skirt Length CB / 后裙长"],
  ["S_skirt_hem",          "S  Skirt Hem / 裙摆"],
  ["T_sleeve_cap",         "T  Sleeve Cap / 袖山高"],
];

function MeasCell({ ymap, mk }: { ymap: Y.Map<FieldEnvelope>; mk: string }) {
  const [val, set] = useYField(ymap, mk);
  return (
    <input type="number" step="0.5" value={val} onChange={(e) => set(e.target.value)}
      className="sf-input" />
  );
}

function CustomMeasRows({ ymap, lang }: { ymap: Y.Map<FieldEnvelope>; lang: string }) {
  const [customKeys, setCustomKeys] = useYField(ymap, "__custom_keys__");
  const keys = storedSizes(customKeys);
  const [newLabel, setNewLabel] = useState("");

  function addRow() {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    const key = "custom_" + trimmed.toLowerCase().replace(/\s+/g, "_") + "_" + Date.now();
    const next = [...keys, key + "|" + trimmed];
    setCustomKeys(JSON.stringify(next));
    setNewLabel("");
  }

  function removeRow(entry: string) {
    setCustomKeys(JSON.stringify(keys.filter(k => k !== entry)));
  }

  return (
    <div>
      {keys.map((entry) => {
        const [key, label] = entry.split("|");
        return (
          <div key={key} className="sf-meas-row" style={{gridTemplateColumns:"minmax(0, 1fr) minmax(80px, 110px) 24px"}}>
            <span className="sf-meas-label">{label}</span>
            <MeasCell ymap={ymap} mk={key} />
            <button onClick={() => removeRow(entry)} className="sf-del-btn">×</button>
          </div>
        );
      })}
      <div style={{display:"flex", gap:8, marginTop:8}}>
        <input
          type="text"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          placeholder={formLabel("custom_measurements", lang)}
          className="sf-input sf-custom-measurement-input"
          style={{flex:"1 1 0", minWidth:0}}
          onKeyDown={e => e.key === "Enter" && addRow()}
        />
        <button onClick={addRow} className="sf-add-btn sf-custom-measurement-add-btn" style={{width:"auto", flex:"0 0 auto"}}>+ {formLabel("add", lang)}</button>
      </div>
    </div>
  );
}

export function MeasurementsSection() {
  const { techPack, languageCode: lang } = useStudio();
  const meas = techPack.measurements;
  const [catField] = useYField(techPack.style_id, "category");
  const [system] = useYField(techPack.metadata, "size_system");
  const [gender] = useYField(techPack.metadata, "size_gender");
  const presetCategory = sizePresets[resolvePreset(system, gender || "women")]?.category;
  // Garment-aware relevance, ported from SpecFlow's
  // filterMeasurementsByCategory(). Irrelevant rows are DIMMED, never cleared
  // and never hidden -- switching category must be non-destructive, and a
  // hidden field holding a value the user cannot see is worse than a dim one.
  const dimmed = new Set<string>(dimmedMeasurementKeys(catField || presetCategory || ""));
  function isDimmed(studioKey: string): boolean {
    const canonical = studioMeasurementKey(studioKey);
    return canonical !== null && dimmed.has(canonical);
  }

  return (
    <Section title={t("s3", lang)}>
      <div className="sf-meas-grid">
        {MEAS.map(([key, label]) => (
          <div
            key={key}
            className="sf-meas-row"
            style={{ opacity: isDimmed(key) ? 0.35 : 1 }}
            aria-label={formLabel(key.slice(2), lang)}
          >
            <span className="sf-meas-label">{key[0]} · {formLabel(key.slice(2), lang)}</span>
            <MeasCell ymap={meas} mk={key} />
          </div>
        ))}
      </div>
      {/* Custom measurements -- previously buried; now a labelled subsection
          with its own affordance, since the controls were effectively
          undiscoverable behind a JSON blob. */}
      <div className="sf-subsection" style={{marginTop: 16}}>{formLabel("custom_measurements", lang)}</div>
      <CustomMeasRows ymap={meas} lang={lang} />
    </Section>
  );
}
